// "Pedidos Coreanos": tuk-tuk tours staffed through the guides' WhatsApp
// group. Everything goes through the backend (email_sender
// Routes/coreanos.js), like services/guideApplications.js: it checks admins
// the usual way and only shows tours to approved guides.
import { authHeaders } from "./authApi";

const API = process.env.VUE_APP_API_URL;

async function api(method, path, body) {
  const response = await fetch(`${API}/coreanos${path}`, {
    method,
    headers: { "Content-Type": "application/json", ...(await authHeaders()) },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  let data = {};
  try {
    data = await response.json();
  } catch (e) {
    // empty or non-JSON answer
  }
  if (!response.ok || data.success === false) throw new Error(data.message || "Algo correu mal. Tenta de novo.");
  return data;
}

export const SITE = "https://guiders.pt";
export const shortLink = (request) => `${SITE.replace("https://", "")}/coreanos/${request.shortId}`;

// ---------- Writes (admin) ----------
export const saveTourRequest = (data) => api("POST", "", data);
export const publishTourRequest = (id) => api("POST", `/${id}/publish`);
// Announce a draft later: date "2026-11-05" + time "09:00" (Lisbon). Until then it
// stays a draft (edit or cancel and no one is told). No date/time removes it.
export const scheduleTourRequest = (id, date = "", time = "") => api("POST", `/${id}/schedule`, { date, time });
// Sends the "Novo tour" message to the Telegram group again (after an error)
export const sendTourMessage = (id) => api("POST", `/${id}/send`);

// Team (admin): "Atribuir já" / "Trocar" (replace), remove, guide from outside the group
// why = { reason: "fewer_tuks" | "asked" | "other", other } when someone leaves (swap, remove)
export const assignGuide = (id, guideId, replace = null, why = {}) => api("POST", `/${id}/assign`, { guideId, replace, ...why });
// Approves everyone in the suggestion
export const approveSuggestion = (id) => api("POST", `/${id}/approve-suggestion`);
export const removeGuideFromTour = (id, guideId, why = {}) => api("POST", `/${id}/remove`, { guideId, ...why });
// Several removals / swaps at once: [{ guideId, to?, reason, other?, force? }]; messages go out once, after saving
export const saveTeamChanges = (id, changes) => api("POST", `/${id}/team`, { changes });
export const addExternalGuide = (id, guide) => api("POST", `/${id}/external`, guide);

// Telegram: { bot, chat, groups, groupsError } / choose the guides' group
export const getTelegram = () => api("GET", "/telegram");
// role: "guides" (new tours) or "admins" (alerts for the team)
export const useTelegramGroup = (chatId, title, role = "guides") => api("POST", "/telegram", { chatId, title, role });
// Answer buttons: Telegram sends every tap to the backend straight away (webhook)
export const enableTelegramButtons = () => api("POST", "/telegram/webhook-setup");
// The pinned "Esta semana" message in the guides' group (off by default)
export const setTelegramWeekMessage = (on) => api("POST", "/telegram/week", { on });
// Personal t.me link that links a guide's account in one tap: { url }
export const getGuideTelegramInvite = (id) => api("POST", `/guides/${id}/telegram-link`);
export const cancelTourRequest = (id) => api("POST", `/${id}/cancel`);

// Admin-only data: { private: { clientWhatsapp, internalNotes }, log: [...] }
export const getAdminData = (id) => api("GET", `/${id}/admin`);
export const listLocations = async () => (await api("GET", "/locations")).locations;

// ---------- Guides ----------
// Guide (phone sign-in) from the link /c/{shortId}
export const guideMe = async () => (await api("GET", "/guide/me")).guide;
export const guideRegister = async (name, whatsapp) => (await api("POST", "/guide/register", { name, whatsapp })).guide;
// "Ligar o meu Telegram": { url } to open the bot (then the guide taps "Começar")
export const linkGuideTelegram = () => api("POST", "/guide/telegram");
// Who am I (also tells if the Telegram link is done)
export const getGuideMe = async () => (await api("GET", "/guide/me")).guide;

// Personal page: { guide, tours } (tours is null until approved)
export const getGuideHome = () => api("GET", "/guide/tours");
export const getGuideTour = (shortId) => api("GET", `/c/${encodeURIComponent(shortId)}`);
// After a date/time change: keep (true) or leave (false)
export const reconfirmTour = (shortId, keep) => api("POST", `/c/${encodeURIComponent(shortId)}/reconfirm`, { keep });
export const respondToTour = (shortId, availability, seats) => api("POST", `/c/${encodeURIComponent(shortId)}/respond`, { availability, seats });
// A guide on the team hands their place to someone available (eligible only)
export const swapTourGuide = (shortId, guideId) => api("POST", `/c/${encodeURIComponent(shortId)}/swap`, { guideId });

// Admin: Guias page
export const listTukGuides = async () => (await api("GET", "/guides")).guides;
// Approving (status "active") also sends the approval email: { emailSent, emailError }
export const setTukGuideStatus = (id, status) => api("POST", `/guides/${id}/status`, { status });
// Approval email for guides approved before it was automatic
export const sendTukGuideApprovalEmail = (id) => api("POST", `/guides/${id}/approval-email`);
export const setTukGuideRating = (id, rating) => api("POST", `/guides/${id}/rating`, { rating });
// Rating history (automatic events) and "Justificar" (stops an event counting)
export const getTukGuideEvents = (id) => api("GET", `/guides/${id}/events`);
export const justifyTukGuideEvent = (id, eventId, justified) => api("POST", `/guides/${id}/events/${eventId}/justify`, { justified });
// After the start: { guideId, status: ok|late|no_show, minutes? }
export const markAttendance = (id, guideId, status, minutes = null) => api("POST", `/${id}/attendance`, { guideId, status, minutes });
// "3,6" (one decimal, Portuguese comma)
export const ratingLabel = (r) => (r == null ? "–" : Number(r).toLocaleString("pt-PT", { maximumFractionDigits: 1, minimumFractionDigits: Number(r) % 1 ? 1 : 0 }));

// ---------- Reads (admin) ----------
// Refreshes every 30 s and when the tab comes back into view
function poll(load, onChange, onError) {
  let stopped = false;
  const run = () => load().then((v) => !stopped && onChange(v), (e) => !stopped && onError && onError(e));
  const onVisible = () => document.visibilityState === "visible" && run();
  run();
  const timer = setInterval(run, 30000);
  document.addEventListener("visibilitychange", onVisible);
  return () => {
    stopped = true;
    clearInterval(timer);
    document.removeEventListener("visibilitychange", onVisible);
  };
}

// List from 60 days ago; each request includes clientWhatsapp
export const watchTourRequests = (onChange, onError) => poll(async () => (await api("GET", "")).requests, onChange, onError);

// One request with { request, private, log, responses }
export const watchTourRequest = (id, onChange, onError) => poll(() => getAdminData(id), onChange, onError);

// ---------- Formatting (Europe/Lisbon, pt-PT) ----------
const DAYS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
const MONTHS = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];

// "Qua, 21 out" from "2026-10-21"
export function dayLabel(dateStr) {
  if (!dateStr) return "";
  const [y, m, d] = dateStr.split("-").map(Number);
  return `${DAYS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()]}, ${d} ${MONTHS[m - 1]}`;
}

export const whenLabel = (r) => `${dayLabel(r.date)} · ${r.startTime}`;

export const toDate = (v) => (v && v.toDate ? v.toDate() : v ? new Date(v) : null);

// "18 out 11:30" / "Hoje 21:43" / "Ontem 16:21", in Lisbon time
export function stamp(value) {
  const date = toDate(value);
  if (!date) return "";
  const fmt = (d, opts) => new Intl.DateTimeFormat("pt-PT", { timeZone: "Europe/Lisbon", ...opts }).format(d);
  const key = (d) => fmt(d, { year: "numeric", month: "2-digit", day: "2-digit" });
  const time = fmt(date, { hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
  if (key(date) === key(new Date())) return `Hoje ${time}`;
  if (key(date) === key(new Date(Date.now() - 864e5))) return `Ontem ${time}`;
  const [d, m] = key(date).split("/");
  return `${Number(d)} ${MONTHS[Number(m) - 1]} ${time}`;
}

export const minutesUntil = (value) => Math.max(0, Math.ceil((toDate(value) - Date.now()) / 60000));

// "1 tuk-tuk" / "3 tuk-tuks"
export const tuksLabel = (n) => `${n} tuk-tuk${n === 1 ? "" : "s"}`;
// "1 disponível" / "2 disponíveis"
export const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

// ---------- Group messages (WhatsApp formatting) ----------
export function newTourMessage(r) {
  const six = r.tuksSixSeatsRequired ? ` (${r.tuksSixSeatsRequired} de 6 lugares)` : "";
  return [
    "Novo tour Coreanos",
    whenLabel(r),
    `Pick-up: ${r.pickup ? r.pickup.label : "…"}`,
    `Drop-off: ${r.dropoff ? r.dropoff.label : "sem drop-off"}`,
    `${tuksLabel(r.tuksTotal)}${six}`,
    ...(r.guidePay != null && r.guidePay !== "" ? [`Pagamento: ${r.guidePay} € por guia`] : []),
    `Diz se podes aqui: ${r.shortId ? shortLink(r) : "guiders.pt/coreanos/…"}`,
  ].join("\n");
}

// ---------- Status for lists and badges ----------
// A tour counts as done 1h30 after it starts
export const DONE_AFTER_MS = 90 * 60000;

export function statusOf(r, now = Date.now()) {
  const counts = r.counts || {};
  const start = toDate(r.startAt);
  if (r.status === "cancelled") return { key: "cancelled", label: "Cancelado", note: "" };
  if (r.status === "completed" || (start && start.getTime() < now - DONE_AFTER_MS)) return { key: "done", label: "Concluído", note: "" };
  if (r.status === "draft") return { key: "draft", label: r.publishAt ? "Agendado" : "Rascunho", note: r.publishAt ? `Anuncia ${stamp(r.publishAt)}` : "Não publicado" };
  const closes = toDate(r.selectionClosesAt);
  if (closes && closes.getTime() > now && !r.selectionDone) {
    return { key: "selecting", label: `Seleção em ${minutesUntil(closes)} min`, note: "" };
  }
  if ((counts.open || 0) > 0) {
    const n = counts.open;
    return { key: "searching", label: "À procura de guias", note: r.sharedAt ? `${tuksLabel(n)} em falta` : "Ainda não partilhado no grupo" };
  }
  if (counts.notReconfirmed) return { key: "warn", label: "Completo", note: plural(counts.notReconfirmed, "não reconfirmou", "não reconfirmaram") };
  return { key: "full", label: "Completo", note: counts.waitlist ? `${counts.waitlist} na lista de espera` : "" };
}

export const waLink = (phone) => `https://wa.me/${String(phone || "").replace(/\D/g, "")}`;
